/**
 * Feature flag client store.
 *
 * Pulls the public flag map from /v1/flags on app boot and exposes a
 * reactive `flags` proxy so any component can `$derived(flags.foo)` and
 * re-render when an admin flips a flag (after the next page load — we
 * don't poll). Code defaults live in `DEFAULTS` below; missing rows
 * never block boot.
 *
 * Usage:
 *   import { flags, flagBool, flagNumber } from '$domain/flags.svelte';
 *   if (flagBool('cloud_save')) { ... }
 *   const cap = flagNumber('solver_max_workers');
 *
 * Adding a new flag is a one-line edit to DEFAULTS plus the call site.
 * Admins can then override via the Admin → Flags tab.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Code-side defaults. Used when the server hasn't returned a row for a
 *  given key yet (or returns nothing because the API is down). The
 *  description is purely documentation — the admin UI shows whatever
 *  description was saved server-side. Entries here advertise "this flag
 *  exists" so the admin can flip them without typing the key by hand. */
export const DEFAULTS = {
  /** Master switch: when false, repository.ts forces local IDB-only and
   *  hides cloud-only UI affordances (share / likes / Save to cloud). */
  cloud_save: { value: 'true', kind: 'bool' as const },
  /** Hides the "Share" button on saved builds without disabling the
   *  underlying share endpoint (existing share links keep working). */
  share_enabled: { value: 'true', kind: 'bool' as const },
  /** Hides the like button on shared builds. Unlikes already given are
   *  preserved — only the UI is suppressed. */
  likes_enabled: { value: 'true', kind: 'bool' as const },
  /** Solver pool size override. SPA caps at 8 to avoid spawning more
   *  workers than CPUs on most laptops. */
  solver_max_workers: { value: '4', kind: 'number' as const },
  /** Hides "rift is missing"-style notes in subclass UI. Use during a
   *  manifest refresh when known fields are absent. */
  show_subclass_notes: { value: 'true', kind: 'bool' as const },
  /** Banner text shown on the home page. Empty = no banner. */
  banner_message: { value: '', kind: 'string' as const },

  // ── DIM-Inspired Enhancement Flags ─────────────────────────────────────
  /** Auto-loadouts: max power, max stat, leveling quick actions. */
  feature_auto_loadouts: { value: 'true', kind: 'bool' as const },
  /** Stat breakdown: show where each stat comes from (armor, mods, MW). */
  feature_stat_breakdown: { value: 'true', kind: 'bool' as const },
  /** Fragment slot validation: detect too many or empty fragment slots. */
  feature_fragment_validation: { value: 'true', kind: 'bool' as const },
  /** Pinned/excluded items: lock items in or exclude from optimization. */
  feature_pinned_excluded: { value: 'true', kind: 'bool' as const },
  /** Stat ranges: min AND max stat constraints (not just floors). */
  feature_stat_ranges: { value: 'true', kind: 'bool' as const },
  /** Overlapping loadout detection: show if result matches saved loadout. */
  feature_loadout_overlap: { value: 'true', kind: 'bool' as const },
  /** CSV export: export loadouts to spreadsheet. */
  feature_csv_export: { value: 'true', kind: 'bool' as const },
  /** Undo/redo: undo and redo actions in the builder. */
  feature_undo_redo: { value: 'true', kind: 'bool' as const },
  /** Vendor items: include Xur/vendor items in optimization. */
  feature_vendor_items: { value: 'false', kind: 'bool' as const },
  /** Search query filtering: filter armor with search syntax. */
  feature_search_filter: { value: 'true', kind: 'bool' as const },
  /** In-game loadout sync: match with Destiny's in-game loadouts. */
  feature_ingame_sync: { value: 'false', kind: 'bool' as const },
  /** Energy cost variants: auto-select cheaper mod energy variants. */
  feature_energy_variants: { value: 'true', kind: 'bool' as const },
  /** Admin override for the Bright Engrams tab layout. JSON of shape
   *  `{ v: 1, cats: { <metaCategory>: [engramHash, ...] } }`. Engrams
   *  listed in a category array appear there in that order; engrams
   *  not listed anywhere fall back to the built-in classifier +
   *  canonical sort. Empty object = full defaults. */
  engram_layout: { value: '{"v":1,"cats":{}}', kind: 'json' as const },
} satisfies Record<string, { value: string; kind: 'bool' | 'string' | 'number' | 'json' }>;

export type FlagKey = keyof typeof DEFAULTS | (string & {});

interface FlagEntry {
  v: string;
  k: string;
}

/** Reactive flag map. Reads return the server-side override if present,
 *  else the code default, else the bare default in the helper. */
let _server: Record<string, FlagEntry> = $state({});
let _loaded = $state(false);

export const flags = {
  get loaded(): boolean {
    return _loaded;
  },
  get raw(): Record<string, FlagEntry> {
    return _server;
  },
};

function entry(key: FlagKey): FlagEntry | null {
  const fromServer = _server[key];
  if (fromServer) return fromServer;
  const def = (DEFAULTS as Record<string, { value: string; kind: string }>)[key];
  if (def) return { v: def.value, k: def.kind };
  return null;
}

export function flagBool(key: FlagKey, fallback = false): boolean {
  const e = entry(key);
  if (!e) return fallback;
  return e.v === 'true';
}

export function flagString(key: FlagKey, fallback = ''): string {
  const e = entry(key);
  if (!e) return fallback;
  return e.v;
}

export function flagNumber(key: FlagKey, fallback = 0): number {
  const e = entry(key);
  if (!e) return fallback;
  const n = Number(e.v);
  return Number.isFinite(n) ? n : fallback;
}

export function flagJson<T>(key: FlagKey, fallback: T): T {
  const e = entry(key);
  if (!e) return fallback;
  try {
    return JSON.parse(e.v) as T;
  } catch {
    return fallback;
  }
}

/** Fetch the public flag map. Called once on boot. Failures are silent
 *  — the SPA falls through to code defaults so a down API never breaks
 *  the app. */
export async function loadFlags(): Promise<void> {
  if (!API_BASE) {
    _loaded = true;
    return;
  }
  try {
    const res = await fetch(`${API_BASE}/v1/flags`);
    if (!res.ok) return;
    const body = (await res.json()) as { flags?: Record<string, FlagEntry> };
    if (body.flags && typeof body.flags === 'object') {
      _server = body.flags;
    }
  } catch {
    // ignore — defaults will be used
  } finally {
    _loaded = true;
  }
}

/** Refresh after an admin write so the admin sees their own change
 *  immediately without a hard reload. Other tabs catch up via the
 *  edge cache TTL (30 s) on next page load. */
export async function refreshFlags(): Promise<void> {
  _loaded = false;
  await loadFlags();
}
